import type { PrimitiveAtom } from 'jotai'
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai'
import type { RefObject, PropsWithChildren } from 'react'
import { createContext, useContext, useRef } from 'react'
import { createCtx } from 'src/utils/contextState'

export const ImageItemListGroup = {
  default: 'default',
  i2vProTailFrame: 'i2v-pro-tail-frame',
}
export type ImageItemListGroup =
  (typeof ImageItemListGroup)[keyof typeof ImageItemListGroup]

export const createImageItemListStateAtom = () =>
  atom<Record<ImageItemListGroup, PrimitiveAtom<string[]>>>({
    [ImageItemListGroup.default]: atom<string[]>([]),
    [ImageItemListGroup.i2vProTailFrame]: atom<string[]>([]),
  })

export const MediaSelectPreviewPanelItemListContext = createContext(
  {} as ReturnType<typeof createImageItemListStateAtom>,
)

/**
 * Functions below are ImageItemList jotai atom access utils
 *
 * Only usable inside a MediaSelectPreviewPanelItemListContext with
 * createImageItemListStateAtom as its value
 * Will throw error otherwise
 */

export const useImageItemListValue = (
  group: ImageItemListGroup = ImageItemListGroup.default,
) => {
  const atomInContext = useContext(MediaSelectPreviewPanelItemListContext)
  const atom = useAtomValue(atomInContext)
  return useAtomValue(atom[group])
}

export const useSetImageItemList = (
  group: ImageItemListGroup = ImageItemListGroup.default,
) => {
  const atomInContext = useContext(MediaSelectPreviewPanelItemListContext)
  const atom = useAtomValue(atomInContext)
  return useSetAtom(atom[group])
}
export const useImageItemList = (
  group: ImageItemListGroup = ImageItemListGroup.default,
) => {
  const atomInContext = useContext(MediaSelectPreviewPanelItemListContext)
  const atom = useAtomValue(atomInContext)
  return useAtom(atom[group])
}
export const useImageItemListCount = (
  group: ImageItemListGroup = ImageItemListGroup.default,
) => {
  const imageList = useImageItemListValue(group)
  return imageList.length
}
export const useImageItemListIsNotEmpty = (
  group: ImageItemListGroup = ImageItemListGroup.default,
) => {
  return useImageItemListCount(group) > 0
}

/**
 * Using ref for performance optimization
 * Map<string, Blob>
 * String starts with `upload-`
 */
export const MediaSelectPreviewPanelItemsRefContext = createContext<
  RefObject<Map<string, { file: Blob; controller: AbortController }> | null>
>({
  current: null,
})

function MediaSelectPreviewPanelItemsRefContextProvider(
  props: PropsWithChildren,
) {
  const ref = useRef(
    new Map<string, { file: Blob; controller: AbortController }>(),
  )
  return (
    <MediaSelectPreviewPanelItemsRefContext.Provider value={ref}>
      {props.children}
    </MediaSelectPreviewPanelItemsRefContext.Provider>
  )
}
export const [
  MediaSelectPreviewPanelItemsProgressContext,
  MediaSelectPreviewPanelItemsProgressContextProvider,
] = createCtx<Record<string, number>>({})

/**
 * Upload progress provider
 * @param props
 * @returns
 */
export function MediaSelectPreviewPanelUploadProvider(
  props: PropsWithChildren,
) {
  return (
    <MediaSelectPreviewPanelItemsRefContextProvider>
      <MediaSelectPreviewPanelItemsProgressContextProvider>
        {props.children}
      </MediaSelectPreviewPanelItemsProgressContextProvider>
    </MediaSelectPreviewPanelItemsRefContextProvider>
  )
}
